import React, { FormEvent, useState, useMemo, useCallback } from 'react';
import Button from '@mui/material/Button';
import { Box, Typography } from '@mui/material';
import { StyleSheet } from 'react-native';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { useMultistepForm } from './useMultistepForm';
import { useEsHover } from '../Animation/useEsHover';
import { Signup } from './Signup';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsVerifyCode } from '../EsVerify/EsVerifyCode';
import { TUserField } from '../../redux/user/actions';
import { ScreenTypes } from '../../module/ThemeManager';
import { strings } from '../../constants';
import { Spinner } from '@chakra-ui/react';
import { OTPPage } from './OTPPage';
import { Interests, TOnChangePayload } from './Interests';
import { validate } from '../../utils/validators';
import { isEmpty } from 'lodash';
import { CountryData } from 'react-phone-input-2';
import { AudmeQRCodeStep } from './AudmeQRCodeStep';
import { useSearchParams } from 'react-router-dom';
import { isBrandAuthFlow } from '../../utils/brandAuthFlow';

export type SignUpdata = {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    userName?: string;
    birth?: string;
    gender?: string;
    country: string;
    phoneNumber: string;
    verificationChannel?: 'sms' | 'whatsapp' | 'audme' | 'email';
    fullName: string;
};

const SIGNUP_DATA: SignUpdata = {
    email: '',
    password: '',
    country: '',
    phoneNumber: '',
    fullName: '',
    firstName: '',
    lastName: '',
    verificationChannel: 'sms',
};

interface ISignUpProps {
    onSubmitSignUpOTP: (data: SignUpdata, code?: string[], callBack?: () => void) => void;
    checkUserDataAvailability: (data: SignUpdata, callBack: () => void, field: TUserField, step: number) => void;
    userNameAvailable: (userName: string) => void;
    getSignUpOTP: (data: SignUpdata, callBack: () => void, resend?: boolean) => void | Promise<unknown>;
    updateUserDetails: (details: TOnChangePayload, callBack?: () => void) => void;
    loading?: boolean;
    otpLoading?: boolean;
    resendInProgress?: boolean;
    formStyle?: React.CSSProperties;
    onSignInClick?: VoidFunction;
    /** Prefill from routes (e.g. BuzzBridge apply → signup) */
    initialFormData?: Partial<SignUpdata>;
    /** Full-page signup: lifted state so hero panel stays in sync. Modal signup: omit for internal state. */
    workspaceChoice?: 'brand' | 'creator';
    onWorkspaceChoiceChange?: (v: 'brand' | 'creator') => void;
}

const EsSignupForm: React.FC<ISignUpProps> = ({
    onSubmitSignUpOTP,
    checkUserDataAvailability,
    updateUserDetails,
    getSignUpOTP,
    loading,
    otpLoading,
    resendInProgress,
    formStyle,
    onSignInClick,
    initialFormData,
    workspaceChoice: workspaceChoiceProp,
    onWorkspaceChoiceChange,
}) => {
    const [searchParams] = useSearchParams();
    const [internalWorkspaceChoice, setInternalWorkspaceChoice] = useState<'brand' | 'creator'>(() => {
        const redirect = searchParams.get('redirect');
        const view = searchParams.get('dashboard_view') ?? searchParams.get('view') ?? searchParams.get('type');
        return isBrandAuthFlow(redirect, view) ? 'brand' : 'creator';
    });
    const workspaceChoice = workspaceChoiceProp ?? internalWorkspaceChoice;
    const setWorkspaceChoice = onWorkspaceChoiceChange ?? setInternalWorkspaceChoice;

    const [data, setData] = useState<SignUpdata>(() => ({ ...SIGNUP_DATA, ...initialFormData }));
    const [details, setDetails] = useState<TOnChangePayload>({ interest: [], user_type: [] });
    const [otp, setOtp] = useState<string[]>();
    const [messagePreference, setMessagePreference] = useState<'sms' | 'whatsapp' | 'audme' | 'email' | undefined>();
    const [countryCode, setCountryCode] = useState<CountryData>();
    const [audmeData, setAudmeData] = useState<{
        qrCode?: string;
        deeplink?: string;
        code?: string;
        token?: string;
    }>({});
    const [shouldNavigateAfterAudmeData, setShouldNavigateAfterAudmeData] = useState(false);

    function updateFields(fields: Partial<SignUpdata>) {
        setData(prev => {
            return { ...prev, ...fields };
        });
    }

    const { themeStyle, device } = useThemeContext();

    const { handleMouseEnter, handleMouseLeave, isHover } = useEsHover();

    const handlePhoneNumberCountryChange = (d?: CountryData, isFocused?: boolean) => {
        setCountryCode(d);
    };

    const handleGetSignUpOTP = (data: SignUpdata, next: () => void, resend?: boolean) => {
        // For audme, we need to intercept the callback to wait for the response
        if (data.verificationChannel === 'audme' && !resend) {
            setShouldNavigateAfterAudmeData(true);
            const result = getSignUpOTP(data, () => {}, resend);
            // If audme is selected and we get a response, store the audme data
            if (result && typeof result.then === 'function') {
                result
                    .then((response: any) => {
                        console.log('audme response', response);
                        if (response?.qrCode && response?.deeplink) {
                            setAudmeData({
                                qrCode: response.qrCode,
                                deeplink: response.deeplink,
                                code: response.code,
                                token: response.token,
                            });
                        } else {
                            // If no QR code, just proceed normally
                            setShouldNavigateAfterAudmeData(false);
                            next();
                        }
                    })
                    .catch(() => {
                        setShouldNavigateAfterAudmeData(false);
                        // Error handling is done in the parent
                    });
            } else {
                setShouldNavigateAfterAudmeData(false);
                next();
            }
        } else {
            // For non-audme channels, proceed normally
            getSignUpOTP(data, next, resend);
        }
    };

    // Navigate to next step after audmeData is set and steps are rebuilt
    React.useEffect(() => {
        if (shouldNavigateAfterAudmeData && audmeData.qrCode && audmeData.deeplink && stepRefs.current) {
            setShouldNavigateAfterAudmeData(false);
            // Use setTimeout to ensure steps have been rebuilt
            setTimeout(() => {
                stepRefs.current?.next();
            }, 0);
        }
    }, [shouldNavigateAfterAudmeData, audmeData.qrCode, audmeData.deeplink]);

    // Build steps array conditionally based on whether audme is selected
    // We'll use a ref to store the next/back functions so they're available when building steps
    const stepRefs = React.useRef<{ next: () => void; back: () => void } | null>(null);

    const countSignupFlowSteps = useCallback((preference: typeof messagePreference, audme: typeof audmeData) => {
        let count = 2;
        if (preference === 'audme' && audme?.qrCode && audme?.deeplink) count += 1;
        count += 1;
        count += 1; // interests step
        return count;
    }, []);

    const stepsArray = useMemo(() => {
        const baseSteps = [
            <Signup
                {...data}
                updateFields={updateFields}
                onCountryChange={handlePhoneNumberCountryChange}
                workspaceChoice={workspaceChoice}
                onWorkspaceChoiceChange={setWorkspaceChoice}
            />,
            <OTPPage
                stepIndex={1}
                totalSteps={countSignupFlowSteps(messagePreference, audmeData)}
                title="Verify your identity"
                subtitle="A security code will be sent to your email address."
                phoneNumber={`+${data.phoneNumber}`}
                email={data.email}
                onSetOTPPreference={val => {
                    setMessagePreference(val);
                    setData(prevData => ({
                        ...prevData,
                        verificationChannel: val,
                    }));
                }}
            />,
        ];

        // If audme is selected and we have the QR code data, add the QR code step
        if (messagePreference === 'audme' && audmeData.qrCode && audmeData.deeplink) {
            baseSteps.push(
                <AudmeQRCodeStep
                    key="audme-qr"
                    qrCode={audmeData.qrCode}
                    deeplink={audmeData.deeplink}
                    onReady={() => stepRefs.current?.next()}
                    onBack={() => stepRefs.current?.back()}
                />,
            );
        }

        // Add the verify code step
        baseSteps.push(
            <EsVerifyCode
                key="verify-code"
                {...data}
                verificationChannel={data.verificationChannel}
                onResendOtp={() => handleGetSignUpOTP(data, () => {}, true)}
                onClickBack={() => stepRefs.current?.back()}
                onChange={(otp: string[]) => setOtp(otp)}
                loading={otpLoading}
                resendInProgress={resendInProgress}
                buttonString="Continue"
                style={{
                    padding: 20,
                }}
            />,
        );

        // Add interests step
        baseSteps.push(<Interests key="interests" onChange={setDetails} />);

        return baseSteps;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        data,
        messagePreference,
        audmeData,
        otpLoading,
        resendInProgress,
        updateFields,
        handlePhoneNumberCountryChange,
        setDetails,
        workspaceChoice,
        setWorkspaceChoice,
        countSignupFlowSteps,
    ]);

    const { steps, currentStepIndex, step, isFirstStep, isLastStep, back, next } = useMultistepForm(stepsArray);

    // Update refs when functions change
    React.useEffect(() => {
        stepRefs.current = { next, back };
    }, [next, back]);

    const styles = dynamicStyles(themeStyle, isHover, device);

    // Calculate the verify code step index based on whether audme step is present
    const getVerifyCodeStepIndex = () => {
        return messagePreference === 'audme' && audmeData.qrCode ? 3 : 2;
    };

    // Calculate the interests step index
    const getInterestsStepIndex = () => {
        return messagePreference === 'audme' && audmeData.qrCode ? 4 : 3;
    };

    function onSubmit(e: FormEvent) {
        e.preventDefault();
        const verifyCodeStepIndex = getVerifyCodeStepIndex();
        const interestsStepIndex = getInterestsStepIndex();

        switch (currentStepIndex) {
            case 0:
                checkUserDataAvailability(data, next, 'email', currentStepIndex);
                break;
            case 1:
                handleGetSignUpOTP(data, next);
                break;
            case verifyCodeStepIndex:
                onSubmitSignUpOTP(data, otp, next);
                break;
            case interestsStepIndex:
                /** Go Home */
                updateUserDetails({ ...details, dashboard_view: workspaceChoice }, next);
                break;
        }
    }
    const _validatePasswordCharacters = (pass: any) => {
        const passwordLength = validate.validatePasswordLength(pass);
        const passwordhasNumber = validate.validatePasswordNumber(pass);
        const passwordHascase = validate.validatePasswordCase(pass);
        return passwordLength && passwordhasNumber && passwordHascase;
    };
    const enableButton = () => {
        if (currentStepIndex === 0) {
            const isValid =
                !isEmpty(data.email) &&
                validate.validateEmail(data.email) &&
                !isEmpty(data.password) &&
                _validatePasswordCharacters(data.password) &&
                !isEmpty(data.phoneNumber) &&
                validate.validatePhoneNumber(data.phoneNumber, countryCode?.countryCode);
            return isValid;
        }

        if (currentStepIndex === 1) {
            return !isEmpty(messagePreference);
        }

        // Skip validation for audme QR code step (step 2 when audme is selected)
        if (messagePreference === 'audme' && currentStepIndex === 2) {
            return true;
        }

        const verifyCodeStepIndex = getVerifyCodeStepIndex();
        if (currentStepIndex === verifyCodeStepIndex) {
            return true;
        }

        const interestsStepIndex = getInterestsStepIndex();
        if (currentStepIndex === interestsStepIndex) {
            return details.interest.length > 0 && details.user_type.length > 0;
        }
    };

    const verificationSelection = messagePreference === 'sms' ? 'SMS' : 'WhatsApp™';

    return (
        <Box>
            <form
                style={{
                    ...styles.form,
                    boxSizing: 'border-box',
                    ...formStyle,
                    ...(isLastStep && { minWidth: device.isDesktop ? '600px' : '300px' }),
                }}
                onSubmit={onSubmit}>
                {currentStepIndex !== 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography style={styles.steps}>
                            STEP {currentStepIndex + 1} OF {steps.length}
                        </Typography>
                        {isLastStep ? (
                            <Button
                                type="submit"
                                disabled={!enableButton()}
                                sx={{
                                    minWidth: 0,
                                    px: 1,
                                    py: 0.25,
                                    textTransform: 'none',
                                    color: theme[themeStyle].highlight,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 14,
                                    '&.Mui-disabled': { color: theme[themeStyle].gray_text, opacity: 0.5 },
                                }}>
                                Done
                            </Button>
                        ) : null}
                    </Box>
                )}
                <Box>{step}</Box>

                {isFirstStep && (
                    <Button
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        style={!enableButton() ? styles.disabledButton : styles.button}
                        disabled={!enableButton() || loading}
                        type="submit">
                        {loading ? (
                            <Spinner h={4} w={4} color={theme[themeStyle].tag_white} />
                        ) : (
                            <Typography style={!enableButton() ? styles.signupDisabled : styles.signup}>
                                {strings.sign_up}
                            </Typography>
                        )}
                    </Button>
                )}

                {isFirstStep && onSignInClick && (
                    <Button
                        onClick={onSignInClick}
                        type="button"
                        sx={{
                            width: '100%',
                            mt: 1.5,
                            borderRadius: '50px',
                            border: `0.5px solid ${theme[themeStyle].highlight}`,
                            backgroundColor: 'transparent',
                            textTransform: 'none',
                            py: 1.2,
                            '&:hover': { backgroundColor: 'transparent' },
                        }}>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 14,
                                color: theme[themeStyle].tag_white,
                            }}>
                            Sign In
                        </Typography>
                    </Button>
                )}

                {isFirstStep && (
                    <Typography style={styles.text}>
                        By clicking Sign Up, you agree to our Terms, Privacy Policy and Cookies Policy. You may receive
                        SMS and/or WhatsApp™ notifications [and or emails] from us and can opt out at any time. Thspian
                        will use {verificationSelection} for verification based on your selection.
                    </Typography>
                )}
                {!isFirstStep &&
                    !isLastStep &&
                    currentStepIndex !== getVerifyCodeStepIndex() &&
                    !(messagePreference === 'audme' && currentStepIndex === 2) && (
                        <Button
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            style={!enableButton() ? styles.disabledButton : styles.button}
                            disabled={!enableButton()}
                            type="submit">
                            <Typography style={!enableButton() ? styles.signupDisabled : styles.signup}>
                                {isLastStep ? 'Finish' : 'Next'}
                            </Typography>
                        </Button>
                    )}
                {!isFirstStep &&
                    !isLastStep &&
                    currentStepIndex !== getVerifyCodeStepIndex() &&
                    !(messagePreference === 'audme' && currentStepIndex === 2) && (
                        <Button style={styles.backButton} onClick={back} type="button">
                            <Typography style={styles.backText}>{strings.go_back}</Typography>
                        </Button>
                    )}
                {isLastStep && (
                    <Button
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        style={!enableButton() ? styles.disabledButton : styles.button}
                        disabled={!enableButton()}
                        type="submit">
                        <Typography style={!enableButton() ? styles.signupDisabled : styles.signup}>
                            {'Done'}
                        </Typography>
                    </Button>
                )}
            </form>
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme, isHover: boolean, device: ScreenTypes) =>
    StyleSheet.create({
        form: {
            width: '100%',
            maxWidth: 500,
            padding: device.isMobile ? 20 : 50,
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: theme[colorScheme].form_black,
            borderRadius: 20,
            marginTop: 15,
        },
        steps: {
            fontSize: 11,
            letterSpacing: 1.98,
            fontWeight: '500',
            color: theme[colorScheme].primary_orange,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        signup: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: isHover ? theme[colorScheme].gray_text : theme[colorScheme].true_black,
            height: 20,
        },
        signupDisabled: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].true_highlight,
            height: 20,
        },
        button: {
            width: '100%',
            height: 44,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 100,
            maxWidth: 400,
            backgroundColor: theme[colorScheme].true_highlight,
            textTransform: 'none',
            alignSelf: 'center',
            marginTop: 10,
        },
        disabledButton: {
            width: '100%',
            height: 44,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 100,
            maxWidth: 400,
            backgroundColor: theme[colorScheme].tag_white_light,
            borderColor: theme[colorScheme].highlight_to_white,
            textTransform: 'none',
            alignSelf: 'center',
            marginTop: 10,
        },
        text: {
            maxWidth: 400,
            width: '100%',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].light_gray_text,
            marginTop: 20,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        backText: {
            textAlign: 'center',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].lightWhite,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        backButton: {
            width: 'fit-content',
            alignSelf: 'center',
            height: 'fit-content',
            paddingHorizontal: 20,
            paddingVertical: 5,
            backgroundColor: theme[colorScheme].transparent,
            textTransform: 'none',
            marginTop: 20,
        },
    });

export { EsSignupForm };
